import Lazy from "@components/common/Lazy";
import Section from "@components/common/Section";
import { useBreakpoints } from "@justhome/common/hooks";
import { ComponentSectionsHeyflow } from "@lib/generated/graphql";
import useApplicationContext from "@modules/application/hooks/useApplicationContext";
import { useRouter } from "next/router";
import Script from "next/script";
import { FC, useCallback, useEffect, useRef, useState } from "react";

interface HeyflowProps {
  data: ComponentSectionsHeyflow;
  index: number;
}

const Heyflow: FC<HeyflowProps> = ({ data, index }) => {
  const { xs, s } = useBreakpoints();
  const heyflowWrapperRef = useRef<HTMLElement>(null);
  const [isActive, setIsActive] = useState(false);
  const handleLoad = useCallback(() => setIsActive(true), []);
  const { state } = useApplicationContext();
  const router = useRouter();
  const leadId = state.lead?.id;
  const urlParams = new URLSearchParams(router.query as Record<string, string>);

  if (leadId) {
    urlParams.set("utm_term", leadId);
  }

  useEffect(() => {
    if (!isActive) {
      return;
    }

    let timeoutId: number;

    const updateFont = () => {
      if (!heyflowWrapperRef.current) {
        return;
      }

      const shadowRoot = heyflowWrapperRef.current.shadowRoot;

      if (shadowRoot) {
        const styles = document.createElement("style");
        styles.innerText = `
            * {
              font-family: SuisseIntl, sans-serif !important; 
              font-weight: 400 !important;
            }
            h3, h4, h5, h6 {
              font-family: 'PSFournier Pro', serif !important; font-style: italic !important; 
            }
          `;
        shadowRoot.appendChild(styles);
      } else {
        timeoutId = window.setTimeout(updateFont, 300);
      }
    };

    updateFont();

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isActive]);

  return (
    <Section
      section={data}
      index={index}
      id={data.heyflowId}
      className="my-2s m:my-3s px-m py-3m bg-neutral-12"
    >
      <Lazy onLoad={handleLoad}>
        <Script src="https://static.heyflow.app/widget/latest/webview.js" />
        <heyflow-wrapper
          url-parameters={urlParams}
          ref={heyflowWrapperRef}
          flow-id={data.heyflowId}
          dynamic-height
          scroll-up-on-navigation={xs || s || undefined}
          style-config='{"width": "100%"}'
        ></heyflow-wrapper>
      </Lazy>
    </Section>
  );
};

export default Heyflow;
